import { useEffect, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { LP_PROC_CSS, LP_PROC_HTML } from "@/lib/hymenoplasty-agra-procedure-lp";
import {
  LP_PROC_GGN_CSS,
  LP_PROC_GGN_HTML,
} from "@/lib/hymenoplasty-gurgaon-procedure-lp";
import {
  LP_PROC_HDW_CSS,
  LP_PROC_HDW_HTML,
} from "@/lib/hymenoplasty-haridwar-procedure-lp";
import { separateLandingPageDisclaimer } from "@/lib/landing-page-disclaimer";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { notifyForm } from "@/lib/notifyForm";

export const hymenoplastyAgraProcedureFaqs = [
  {
    q: "What is the hymen, exactly?",
    a: "The hymen is a thin membrane at the vaginal opening. It varies naturally from woman to woman and can stretch or tear from sport, tampon use, exercise or intercourse — so it is not a reliable indicator of anything.",
  },
  {
    q: "What happens during the hymenoplasty procedure in Agra?",
    a: "The remaining edges of the hymenal tissue are gently approximated and repaired with fine dissolvable sutures. It is a day-care procedure done under local anaesthesia or light sedation, and most patients go home the same day.",
  },
  {
    q: "How long does the procedure take?",
    a: "The surgery itself usually takes about 30–45 minutes. Including preparation and observation, you are typically at the hospital for a few hours.",
  },
  {
    q: "Is hymenoplasty painful?",
    a: "You will not feel pain during the procedure because of anaesthesia. Mild soreness for a few days afterwards is normal and settles with the medication your doctor prescribes.",
  },
  {
    q: "How long is the recovery?",
    a: "Most patients return to routine activities within a few days. Complete healing usually takes around 4–6 weeks, with a follow-up visit to confirm healing is on track.",
  },
  {
    q: "Will anyone know I had the procedure?",
    a: "No. Your consultation and records are fully confidential, the sutures dissolve on their own, and there are no visible external signs.",
  },
  {
    q: "Is hymenoplasty legal in India?",
    a: "Yes. It is a legal, medically recognised cosmetic gynaecological procedure performed by licensed gynaecologists.",
  },
  {
    q: "Does it affect fertility or future pregnancy?",
    a: "No. Hymenoplasty is a localised repair and does not involve the uterus, ovaries or fallopian tubes, so fertility is unaffected.",
  },
];

function istHour(): number {
  try {
    return parseInt(
      new Date().toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        hour12: false,
      }),
      10,
    );
  } catch {
    return 12;
  }
}

export const hymenoplastyGurgaonProcedureFaqs = hymenoplastyAgraProcedureFaqs.map((f) => ({
  q: f.q.replace("in Agra", "in Gurgaon"),
  a: f.a,
}));

export const hymenoplastyHaridwarProcedureFaqs = hymenoplastyAgraProcedureFaqs.map((f) => ({
  q: f.q.replace("in Agra", "in Haridwar"),
  a: f.a,
}));

export function HymenoplastyAgraProcedurePage({
  city = "Agra",
}: {
  city?: "Agra" | "Gurgaon" | "Haridwar";
}) {
  const lpCss =
    city === "Gurgaon" ? LP_PROC_GGN_CSS : city === "Haridwar" ? LP_PROC_HDW_CSS : LP_PROC_CSS;
  const lpHtml =
    city === "Gurgaon" ? LP_PROC_GGN_HTML : city === "Haridwar" ? LP_PROC_HDW_HTML : LP_PROC_HTML;
  const { contentHtml, disclaimerHtml } = separateLandingPageDisclaimer(lpHtml);
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");

  useEffect(() => {
    const h = istHour();
    setCtaMode(h >= 9 && h < 20 ? "active" : "off");
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];

    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");

    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
      const dlg = modal.querySelector<HTMLElement>(".modal-dialog");
      if (dlg) dlg.scrollTop = 0;
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) {
        document.documentElement.classList.remove("modal-open");
      }
    };
    const closeAll = () => {
      root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal);
    };
    const openLeadForm = (doctor = "") => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = doctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      if (thankYou) thankYou.classList.remove("active");
      openModal(leadModal);
      setTimeout(() => {
        root.querySelector<HTMLInputElement>("#m-name")?.focus({ preventScroll: true });
      }, 350);
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const closeBtn = target?.closest("[data-modal-close]");
      if (closeBtn) {
        e.preventDefault();
        closeModal(closeBtn.closest(".modal-backdrop") as HTMLElement);
        return;
      }
      const opener = target?.closest("[data-open-form]");
      if (opener) {
        e.preventDefault();
        openLeadForm(opener.getAttribute("data-doctor") || "");
      }
    };
    root.addEventListener("click", onClick);
    cleanups.push(() => root.removeEventListener("click", onClick));

    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((bd) => {
      const handler = (e: Event) => {
        if (e.target === bd) closeModal(bd);
      };
      bd.addEventListener("click", handler);
      cleanups.push(() => bd.removeEventListener("click", handler));
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", onKey);
    cleanups.push(() => document.removeEventListener("keydown", onKey));

    const isDesktop = () =>
      window.matchMedia("(min-width: 900px) and (pointer: fine)").matches;
    const isOffHours = () => root.dataset.ctaMode === "off";

    const showToast = (msg: string) => {
      const existing = document.getElementById("off-hours-toast");
      if (existing) existing.remove();
      const t = document.createElement("div");
      t.id = "off-hours-toast";
      t.setAttribute("role", "status");
      t.textContent = msg;
      root.appendChild(t);
      void t.offsetWidth;
      t.classList.add("show");
      setTimeout(() => {
        t.classList.remove("show");
        setTimeout(() => t.remove(), 350);
      }, 4500);
    };

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (e: Event) => {
        if (isOffHours()) {
          e.preventDefault();
          if (
            link.classList.contains("top-phone") ||
            link.classList.contains("foot-phone") ||
            link.classList.contains("loc-phone-link")
          ) {
            showToast(
              "Our team is not active right now. Fill the form and we will call you back privately in active hours.",
            );
          }
          openLeadForm();
          return;
        }
        if (isDesktop()) {
          e.preventDefault();
          openModal(callModal);
        }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });

    const submitForm = (form: HTMLFormElement, inline: boolean) => {
      const data = new FormData(form);
      const name = String(data.get("name") ?? "").trim();
      const phone = String(data.get("phone") ?? "").trim();
      void notifyForm({
        formType: "Private Hymenoplasty Consultation",
        source: inline
          ? `${city} Hymenoplasty Procedure Page (inline form)`
          : `${city} Hymenoplasty Procedure Page`,
        fields: [
          { label: "Name", value: name },
          { label: "Phone", value: phone },
          { label: "Treatment", value: "Hymenoplasty" },
          { label: "City", value: city },
          { label: "Doctor", value: String(data.get("doctor") ?? "") },
          { label: "Page", value: window.location.href },
        ],
      });
      const tyName = root.querySelector("#ty-name");
      if (tyName) tyName.textContent = (name || "friend").split(" ")[0]!;
      form.reset();
      if (formContainer) formContainer.style.display = "none";
      thankYou?.classList.add("active");
      openModal(leadModal);
      const dlg = leadModal?.querySelector<HTMLElement>(".modal-dialog");
      if (dlg) dlg.scrollTop = 0;
    };

    const forms: Array<[HTMLFormElement, boolean]> = [];
    const modalForm = root.querySelector<HTMLFormElement>("#modal-form");
    if (modalForm) forms.push([modalForm, false]);
    const inlineForm = root.querySelector<HTMLFormElement>("form.inline-form-right");
    if (inlineForm) forms.push([inlineForm, true]);
    forms.forEach(([form, inline]) => {
      const handler = (e: Event) => {
        e.preventDefault();
        submitForm(form, inline);
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    });

    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const src = mapFrame.getAttribute("data-map-src");
            if (src && !mapFrame.querySelector("iframe")) {
              const iframe = document.createElement("iframe");
              iframe.src = src;
              iframe.width = "100%";
              iframe.height = "100%";
              iframe.loading = "lazy";
              iframe.style.border = "0";
              iframe.title = `Healic Multispeciality Hospital ${city} — map`;
              iframe.referrerPolicy = "no-referrer-when-downgrade";
              mapFrame.querySelector(".map-placeholder-deferred")?.remove();
              mapFrame.appendChild(iframe);
            }
            observer.disconnect();
          });
        },
        { rootMargin: "300px" },
      );
      observer.observe(mapFrame);
      cleanups.push(() => observer.disconnect());
    }

    return () => {
      cleanups.forEach((fn) => fn());
      document.documentElement.classList.remove("modal-open");
    };
  }, [city]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: lpCss + LP_CHROME_CSS }} />
      <div className="lp-chrome">
        <SiteHeader />
      </div>
      <div
        ref={rootRef}
        data-cta-mode={ctaMode}
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
      <div className="lp-chrome">
        <SiteFooter />
      </div>
      {disclaimerHtml && <div dangerouslySetInnerHTML={{ __html: disclaimerHtml }} />}
    </>
  );
}
